import { create } from "zustand";

export const SUPERUSER_ORGANIZATION_HEADER = "X-Vendistri-Organization";
export const SUPERUSER_WRITE_HEADER = "X-Vendistri-Superuser-Write";
export const SUPERUSER_BILLING_HEADER = "X-Vendistri-Superuser-Billing";
export const SUPERUSER_REASON_HEADER = "X-Vendistri-Superuser-Reason";
export const SUPERUSER_SUPPORT_ACCOUNT_HEADER = "X-Vendistri-Support-Account";
export const SUPERUSER_HEADER_ENABLED = "true";

export type SuperuserOrganizationOption = {
  id: string;
  title: string;
  ownerEmail?: string | null;
  logoUrl?: string | null;
  useDefaultLogo?: boolean;
};

export type SuperuserSupportAccount = {
  id: string;
  label: string;
  email: string;
  role: string;
  source: "member" | "location_contact";
  status?: string | null;
  canSelect: boolean;
  disabledReason?: string | null;
};

export type SuperuserContextState = {
  enabled: boolean;
  selectedOrg: SuperuserOrganizationOption | null;
  selectedAccount: SuperuserSupportAccount | null;
  memberAccounts: SuperuserSupportAccount[];
  locationContactAccounts: SuperuserSupportAccount[];
  supportAccountsLoading: boolean;
  supportAccountsError: string | null;
  writeEnabled: boolean;
  billingEnabled: boolean;
  reason: string;
  superuserBarVisible: boolean;
  writeAttentionKey: number;
  billingAttentionKey: number;
  setEnabled: (enabled: boolean) => void;
  setSelectedOrg: (organization: SuperuserOrganizationOption | null) => void;
  setSelectedAccount: (account: SuperuserSupportAccount | null) => void;
  setSupportAccountsLoading: (loading: boolean) => void;
  setSupportAccounts: (accounts: {
    members: SuperuserSupportAccount[];
    locationContacts: SuperuserSupportAccount[];
  }, selectedAccount?: SuperuserSupportAccount | null) => void;
  setSupportAccountsError: (message: string | null) => void;
  clearSelectedAccount: () => void;
  updateSelectedOrg: (organization: Partial<SuperuserOrganizationOption> & { id: string }) => void;
  setWriteEnabled: (enabled: boolean) => void;
  setBillingEnabled: (enabled: boolean) => void;
  setReason: (reason: string) => void;
  setSuperuserBarVisible: (visible: boolean) => void;
  requestWriteAttention: () => void;
  requestBillingAttention: () => void;
  reset: () => void;
};

const SUPERUSER_BAR_VISIBLE_KEY = "vendistri.superuserBarVisible";
const SUPERUSER_SESSION_KEY = "vendistri.superuserSupportContext";

type PersistedSuperuserSession = {
  selectedOrg?: SuperuserOrganizationOption | null;
  selectedAccount?: SuperuserSupportAccount | null;
};

const readSuperuserBarVisible = () => {
  try {
    return window.localStorage.getItem(SUPERUSER_BAR_VISIBLE_KEY) !== "false";
  } catch (_error) {
    return true;
  }
};

const normalizePersistedSupportAccount = (
  account?: SuperuserSupportAccount | null
): SuperuserSupportAccount | null => {
  if (!account?.id) return null;
  return {
    ...account,
    canSelect: account.canSelect !== false,
    disabledReason: account.canSelect === false ? account.disabledReason : null,
  };
};

const readSupportSession = (): PersistedSuperuserSession => {
  try {
    const raw = window.sessionStorage.getItem(SUPERUSER_SESSION_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as PersistedSuperuserSession;
    if (!parsed?.selectedOrg?.id) return {};
    return {
      selectedOrg: parsed.selectedOrg,
      selectedAccount: normalizePersistedSupportAccount(parsed.selectedAccount),
    };
  } catch (_error) {
    return {};
  }
};

const writeSupportSession = (updates: PersistedSuperuserSession) => {
  try {
    const selectedOrg = updates.selectedOrg?.id ? updates.selectedOrg : null;
    const selectedAccount =
      selectedOrg ? normalizePersistedSupportAccount(updates.selectedAccount) : null;
    if (!selectedOrg) {
      window.sessionStorage.removeItem(SUPERUSER_SESSION_KEY);
      return;
    }
    window.sessionStorage.setItem(
      SUPERUSER_SESSION_KEY,
      JSON.stringify({ selectedOrg, selectedAccount })
    );
  } catch (_error) {
    // ignore storage failures
  }
};

export function seedSuperuserSupportSession(
  storage: Pick<Storage, "setItem">,
  selectedOrg: SuperuserOrganizationOption,
  selectedAccount: SuperuserSupportAccount | null = null,
) {
  storage.setItem(
    SUPERUSER_SESSION_KEY,
    JSON.stringify({ selectedOrg, selectedAccount }),
  );
}

const clearSupportSession = () => writeSupportSession({ selectedOrg: null });

const supportState = (session: PersistedSuperuserSession = {}) => ({
  enabled: false,
  selectedOrg: session.selectedOrg || null,
  selectedAccount: session.selectedAccount || null,
  memberAccounts: [],
  locationContactAccounts: [],
  supportAccountsLoading: !!session.selectedOrg,
  supportAccountsError: null,
  writeEnabled: false,
  billingEnabled: false,
  reason: "",
  writeAttentionKey: 0,
  billingAttentionKey: 0,
});

const initialState = () => ({
  ...(() => {
    const session = readSupportSession();
    return {
      ...supportState(session),
      enabled: !!session.selectedOrg,
    };
  })(),
  superuserBarVisible: readSuperuserBarVisible(),
});

export const useSuperuserContextStore = create<SuperuserContextState>((set) => ({
  ...initialState(),
  setEnabled: (enabled) => {
    if (!enabled) {
      clearSupportSession();
    }
    set((state) => ({
      enabled,
      selectedOrg: enabled ? state.selectedOrg : null,
      selectedAccount: enabled ? state.selectedAccount : null,
      memberAccounts: enabled ? state.memberAccounts : [],
      locationContactAccounts: enabled ? state.locationContactAccounts : [],
      supportAccountsLoading: enabled ? state.supportAccountsLoading : false,
      supportAccountsError: enabled ? state.supportAccountsError : null,
      writeEnabled: enabled ? state.writeEnabled : false,
      billingEnabled: enabled ? state.billingEnabled : false,
      reason: enabled ? state.reason : "",
    }));
  },
  setSelectedOrg: (selectedOrg) => {
    writeSupportSession({ selectedOrg, selectedAccount: null });
    set({
      selectedOrg,
      selectedAccount: null,
      memberAccounts: [],
      locationContactAccounts: [],
      supportAccountsLoading: !!selectedOrg,
      supportAccountsError: null,
      enabled: selectedOrg ? true : false,
      writeEnabled: false,
      billingEnabled: false,
    });
  },
  setSelectedAccount: (selectedAccount) =>
    set((state) => {
      writeSupportSession({ selectedOrg: state.selectedOrg, selectedAccount });
      return { selectedAccount };
    }),
  setSupportAccountsLoading: (supportAccountsLoading) =>
    set({ supportAccountsLoading }),
  setSupportAccounts: ({ members, locationContacts }, selectedAccount) =>
    set((state) => {
      const nextSelectedAccount =
        selectedAccount === undefined ? state.selectedAccount : selectedAccount;
      writeSupportSession({
        selectedOrg: state.selectedOrg,
        selectedAccount: nextSelectedAccount,
      });
      return {
        memberAccounts: members,
        locationContactAccounts: locationContacts,
        selectedAccount: nextSelectedAccount,
        supportAccountsLoading: false,
        supportAccountsError: null,
      };
    }),
  setSupportAccountsError: (supportAccountsError) =>
    set({ supportAccountsError, supportAccountsLoading: false }),
  clearSelectedAccount: () =>
    set((state) => {
      writeSupportSession({ selectedOrg: state.selectedOrg, selectedAccount: null });
      return { selectedAccount: null };
    }),
  updateSelectedOrg: (organization) =>
    set((state) =>
      state.selectedOrg?.id === organization.id
        ? { selectedOrg: { ...state.selectedOrg, ...organization } }
        : {},
    ),
  setWriteEnabled: (writeEnabled) =>
    set((state) => ({
      writeEnabled: writeEnabled && !!state.reason.trim(),
    })),
  setBillingEnabled: (billingEnabled) =>
    set((state) => ({
      billingEnabled: billingEnabled && !!state.reason.trim(),
    })),
  setReason: (reason) =>
    set((state) => {
      const hasReason = !!reason.trim();
      return {
        reason,
        writeEnabled: hasReason ? state.writeEnabled : false,
        billingEnabled: hasReason ? state.billingEnabled : false,
      };
    }),
  setSuperuserBarVisible: (superuserBarVisible) => {
    try {
      window.localStorage.setItem(
        SUPERUSER_BAR_VISIBLE_KEY,
        superuserBarVisible ? "true" : "false",
      );
    } catch (_error) {
      // ignore storage failures
    }
    set({ superuserBarVisible });
  },
  requestWriteAttention: () =>
    set((state) => ({ writeAttentionKey: state.writeAttentionKey + 1 })),
  requestBillingAttention: () =>
    set((state) => ({ billingAttentionKey: state.billingAttentionKey + 1 })),
  reset: () => {
    clearSupportSession();
    set(initialState());
  },
}));

export function getSuperuserRequestHeaders(): HeadersInit {
  const state = useSuperuserContextStore.getState();
  if (!state.enabled || !state.selectedOrg) return {};
  const headers: Record<string, string> = {
    [SUPERUSER_ORGANIZATION_HEADER]: state.selectedOrg.id,
  };
  if (state.selectedAccount) {
    headers[SUPERUSER_SUPPORT_ACCOUNT_HEADER] = state.selectedAccount.id;
  }
  if (state.writeEnabled) {
    headers[SUPERUSER_WRITE_HEADER] = SUPERUSER_HEADER_ENABLED;
  }
  if (state.billingEnabled) {
    headers[SUPERUSER_BILLING_HEADER] = SUPERUSER_HEADER_ENABLED;
  }
  const reason = state.reason.trim();
  if (reason && (state.writeEnabled || state.billingEnabled)) {
    headers[SUPERUSER_REASON_HEADER] = reason;
  }
  return headers;
}

export function getSuperuserSupportContextKey(): string {
  const state = useSuperuserContextStore.getState();
  if (!state.enabled || !state.selectedOrg) return "account";
  return `support:${state.selectedOrg.id}:${state.selectedAccount?.id || ""}`;
}

export function superuserBillingAccessEnabled(): boolean {
  const state = useSuperuserContextStore.getState();
  return !state.enabled || !state.selectedOrg || state.billingEnabled;
}

export function isSuperuserSupportAccountPendingState(
  state: Pick<
    SuperuserContextState,
    "enabled" | "selectedOrg" | "selectedAccount" | "supportAccountsLoading"
  >
): boolean {
  return Boolean(
    state.enabled &&
      state.selectedOrg &&
      state.supportAccountsLoading &&
      !state.selectedAccount
  );
}

export function isSuperuserSupportAccountReadyState(
  state: Pick<
    SuperuserContextState,
    | "enabled"
    | "selectedOrg"
    | "selectedAccount"
    | "supportAccountsLoading"
    | "supportAccountsError"
  >
): boolean {
  return Boolean(
    !state.enabled ||
      !state.selectedOrg ||
      (!state.supportAccountsLoading &&
        (state.selectedAccount || state.supportAccountsError))
  );
}

export function isSuperuserSupportAccountPending(): boolean {
  return isSuperuserSupportAccountPendingState(
    useSuperuserContextStore.getState()
  );
}
